import React from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styles from './AdminDashboard.module.css';
import { User } from '../services/api';
import { hasAnyRole } from '../utils/permissions';

interface AdminDashboardProps {
  user: User | null;
  roles: string[];
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ user, roles }) => {
  const navigate = useNavigate();

  return (
      <div className="container mt-4">
        <nav aria-label="breadcrumb">
          <ol className={`breadcrumb`}>
            <li className={`breadcrumb-item`}>
              <span><i className="bi bi-house-door me-1"></i>Admin</span>
            </li>
          </ol>
        </nav>

        <div className="row g-4">
          {/* User Management Card */}
          {hasAnyRole(roles, ['Admin', 'User Admin', 'User Role Admin']) && (
            <div className="col-12 col-md-4">
              <div className={`card shadow-lg ${styles.card}`}>
                <div className={`card-body ${styles.cardBody}`}>
                  <i className={`bi bi-people display-4 text-primary mb-3 ${styles.cardIcon}`}></i>
                  <h5 className={`card-title ${styles.cardTitle}`}>User Management</h5>
                  <Link to="/admin/users" className={`btn btn-outline-primary ${styles.actionButton}`}>Manage Users</Link>
                </div>
              </div>
            </div>
          )}

          {/* Firm Management Card */}
          {hasAnyRole(roles, ['Admin', 'Firm Admin']) && (
            <div className="col-12 col-md-4">
              <div className={`card shadow-lg ${styles.card}`}>
                <div className={`card-body ${styles.cardBody}`}>
                  <i className={`bi bi-building display-4 text-primary mb-3 ${styles.cardIcon}`}></i>
                  <h5 className={`card-title ${styles.cardTitle}`}>Firm Management</h5>
                  <Link to="/admin/firms" className={`btn btn-outline-primary ${styles.actionButton}`}>Manage Firms</Link>
                </div>
              </div>
            </div>
          )}

          {/* Location Management Card */}
          {hasAnyRole(roles, ['Admin', 'Location Admin']) && (
            <div className="col-12 col-md-4">
              <div className={`card shadow-lg ${styles.card}`}>
                <div className={`card-body ${styles.cardBody}`}>
                  <i className={`bi bi-geo-alt display-4 text-success mb-3 ${styles.cardIcon}`}></i>
                  <h5 className={`card-title ${styles.cardTitle}`}>Location Management</h5>
                  <Link to="/admin/locations" className={`btn btn-outline-success ${styles.actionButton}`}>
                    {hasAnyRole(roles, ['Admin', 'Location Admin']) ? 'Manage' : 'View'} Locations
                  </Link>
                </div>
              </div>
            </div>
          )}

          

          {/* Error Management Card */}
          {hasAnyRole(roles, ['Admin', 'Error Admin']) && (
            <div className="col-12 col-md-4">
              <div className={`card shadow-lg ${styles.card}`}>
                <div className={`card-body ${styles.cardBody}`}>
                  <i className={`bi bi-exclamation-triangle display-4 text-danger mb-3 ${styles.cardIcon}`}></i>
                  <h5 className={`card-title ${styles.cardTitle}`}>Error Management</h5>
                  <Link to="/admin/errors" className={`btn btn-outline-danger ${styles.actionButton}`}>Manage Errors</Link>
                </div>
              </div>
            </div>
          )}

          {/* Job Management Card */}
          {hasAnyRole(roles, ['Admin', 'Job Admin', 'Log Admin']) && (
            <div className="col-12 col-md-4">
              <div className={`card shadow-lg ${styles.card}`}>
                <div className={`card-body ${styles.cardBody}`}>
                  <i className={`bi bi-gear display-4 text-info mb-3 ${styles.cardIcon}`}></i>
                  <h5 className={`card-title ${styles.cardTitle}`}>Job Management</h5>
                  <Link to="/admin/jobs" className={`btn btn-outline-info ${styles.actionButton}`}>
                    {hasAnyRole(roles, ['Admin', 'Job Admin']) ? 'Manage' : 'View'} Jobs
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Job Logs Card */}
          {hasAnyRole(roles, ['Admin', 'Log Admin']) && (
            <div className="col-12 col-md-4">
              <div className={`card shadow-lg ${styles.card}`}>
                <div className={`card-body ${styles.cardBody}`}>
                  <i className={`bi bi-file-text display-4 text-warning mb-3 ${styles.cardIcon}`}></i>
                  <h5 className={`card-title ${styles.cardTitle}`}>Job Logs</h5>
                  <Link to="/admin/job-logs" className={`btn btn-outline-warning ${styles.actionButton}`}>View Job Logs</Link>
                </div>
              </div>
            </div>
          )}

          {/* Roles Management Card */}
          {hasAnyRole(roles, ['Admin', 'Role Admin', 'User Role Admin']) && (
            <div className="col-12 col-md-4">
              <div className={`card shadow-lg ${styles.card}`}>
                <div className={`card-body ${styles.cardBody}`}>
                  <i className={`bi bi-person-gear display-4 text-info mb-3 ${styles.cardIcon}`}></i>
                  <h5 className={`card-title ${styles.cardTitle}`}>Roles Management</h5>
                  <Link to="/admin/roles" className={`btn btn-outline-info ${styles.actionButton}`}>
                    {hasAnyRole(roles, ['Admin', 'Role Admin']) ? 'Manage' : 'View'} Roles
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* API Key Management Card */}
          {hasAnyRole(roles, ['Admin', 'API Admin']) && (
            <div className="col-12 col-md-4">
              <div className={`card shadow-lg ${styles.card}`}>
                <div className={`card-body ${styles.cardBody}`}>
                  <i className={`bi bi-key display-4 text-dark mb-3 ${styles.cardIcon}`}></i>
                  <h5 className={`card-title ${styles.cardTitle}`}>API Key Management</h5>
                  <Link to="/admin/api-keys" className={`btn btn-outline-dark ${styles.actionButton}`}>Manage API Keys</Link>
                </div>
              </div>
            </div>
          )}

        
          {/* Reader Notice */}
          {roles.includes('Reader') && !roles.includes('Admin') && (
            <div className="col-12">
              <div className="alert alert-info">
                <i className="bi bi-info-circle me-2"></i>
                Sie haben Reader-Berechtigung. Sie können alle Bereiche einsehen, aber keine Änderungen vornehmen.
              </div>
            </div>
          )}

        </div>
      </div>

  );
};

export default AdminDashboard;
